const { getElement } = window.BAO.Utils

/**
 * @class
 * @description Handles the automatic addition and removal of gift items from cart
 */
class GiftWithPurchase {
  constructor () {
    this.sections = this.onCartPage
      ? window.BAO.sectionRenderer.cartSections
      : window.BAO.sectionRenderer.pushCartSections

    this.setupListeners()
    this.init()
  }

  /**
   * @function
   * @description Sets up the required event handlers.
   *
   * @returns {void}
   */
  setupListeners () {
    window.BAO.eventBus.addEventListener(
      window.BAO.EVENTS.PUSH_CART.CHANGED,
      this.init.bind(this),
    )
  }

  /**
   * @description Initialises module.
   *
   * @returns {Promise<void>}
   */
  async init () {
    this.data = await this.fetchData()

    if (this.data.is_cart_empty) {
      this.configurationIdStorage = []

      return
    }

    const itemsToAdd = this.itemsToAddToCart(this.data.configurations)
    const itemsToRemove = this.itemsToRemoveFromCart(this.data.configurations)

    await this.addEligibleItems(itemsToAdd)
    await this.removeIneligibleItems(itemsToRemove)
  }

  /**
   * @description Returns the data required by this module.
   *
   * @returns {Promise<GiftWithPurchaseData>}
   */
  async fetchData () {
    try {
      const response = await fetch(
        `${window.Shopify.routes.root}cart?view=gift-with-purchase`,
      )

      /** @type {FetchResult} */
      const result = await response.json()

      if (result?.status === 'success') return result.data

      return {
        configurations: [],
      }
    } catch (err) {
      console.error(err)
    }
  }

  /**
   * @description Returns items to add to cart.
   *
   * @param {GiftWithPurchaseConfiguration[]} configurations Gift with Purchase configurations.
   * @returns {PotentialCartItem[]}
   */
  itemsToAddToCart (configurations) {
    const eligibleConfigurations = configurations.filter(
      configuration =>
        configuration.is_eligible &&
        configuration.type === 'AUTOMATIC' &&
        !this.configurationIdStorage.includes(configuration.id),
    )
    const items = eligibleConfigurations.flatMap(configuration =>
      configuration.actions.flatMap(action => action.items),
    )

    // Add eligible configuration ids to storage so prevent processing these configurations again
    this.addToConfigurationIdStorage(
      eligibleConfigurations.map(configuration => configuration.id),
    )

    const giftVariants = items
      .flatMap(item =>
        item.variants.filter(
          variant => !variant.is_in_cart && variant.available,
        ),
      )
      .filter(Boolean)
    const giftVariantMap = this.giftVariantMap(giftVariants)

    return [...giftVariantMap.values()].map(value => {
      return {
        id: value.id,
        properties: value.properties,
        quantity: value.quantity,
      }
    })
  }

  /**
   * @description Returns item keys to remove from cart.
   *
   * @param {GiftWithPurchaseConfiguration[]} configurations Gift with Purchase configurations.
   * @returns {string[]}
   */
  itemsToRemoveFromCart (configurations) {
    const ineligibleConfigurations = configurations.filter(
      configuration => !configuration.is_eligible,
    )

    const items = ineligibleConfigurations.flatMap(configuration =>
      configuration.actions.flatMap(action => action.items),
    )

    const keys = items.flatMap(item =>
      item.variants
        .filter(variant => variant.line_item_key.length)
        .flatMap(variant => variant.line_item_key),
    )
    const uniqueKeys = [...new Set(keys)]

    return uniqueKeys
  }

  /**
   * Creates a Map with Gift Variant ids as keys and Gift Variant objects as values.
   *
   * @param {GiftVariant[]} giftVariants - Array of gift variants objects.
   * @returns {Map<GiftVariant['id'], GiftVariant>} Map of variant IDs to their corresponding variants.
   */
  giftVariantMap (giftVariants) {
    return giftVariants.reduce(
      /** @type {VariantMapReducer} */
      (accumulator, giftVariant) => {
        if (accumulator.has(giftVariant.id)) {
          const variant = accumulator.has(giftVariant.id)

          if (variant.quantity < giftVariant.quantity) {
            accumulator.set(giftVariant.id, giftVariant)

            return accumulator
          }
        }

        accumulator.set(giftVariant.id, giftVariant)

        return accumulator
      },
      new Map(),
    )
  }

  /**
   * @description Adds items to cart.
   *
   * @param {PotentialCartItem[]} items Items to add to cart.
   * @returns {Promise<void>}
   */
  async addEligibleItems (items) {
    if (!items.length) return

    await window.BAO.Cart.addItem(items)
  }

  /**
   * @description Remove items to cart.
   *
   * @param {string[]} keys An array of line item keys to remove from cart.
   * @returns {Promise<void>}
   */
  async removeIneligibleItems (keys) {
    if (!keys.length) return

    const state = await window.BAO.Cart.removeItems(keys, {
      sections: this.mappedSections,
    })

    if ('sections' in state) this.renderCartSections(state.sections)
  }

  /**
   * @description Renders sections on cart page.
   *
   * @param {Object<string, string>} sections An object containing sections where the key is the id of a section and value is the html of the section.
   * @returns {void}
   */
  renderCartSections (sections) {
    const sectionsToRender = this.sections
      .map(section => {
        const sectionToRender = sections[section.section]

        if (!sectionToRender) return null

        return {
          ...section,
          html: sections[section.section],
        }
      })
      .filter(Boolean)

    window.BAO.sectionRenderer.renderMultiple(sectionsToRender)
  }

  /**
   * @description Adds an array of configuration ids to storage.
   *
   * @param {GiftWithPurchaseConfiguration["id"][]} ids An array of gift with purchase configuration ids to add to storage.
   * @returns {void}
   */
  addToConfigurationIdStorage (ids) {
    this.configurationIdStorage = [
      ...new Set([...this.configurationIdStorage, ...ids]),
    ]
  }

  /**
   * @description A map of sections to be rendered.
   *
   * @returns {Array} Array of sections to render.
   */
  get mappedSections () {
    return this.sections.map(s => s.section)
  }

  /**
   * @description Used to determine if the current page is the cart page.
   *
   * @returns {HtmlElement} The `cart-items` custom element
   */
  get cart () {
    return getElement('cart-items')
  }

  /**
   * @description Whether or not the current page is the cart page.
   *
   * @returns {boolean}
   */
  get onCartPage () {
    return this.cart != null
  }

  /**
   * @description An array of gift with purchase configuration ids.
   *
   * @returns {GiftWithPurchaseConfiguration["id"][]}
   */
  get configurationIdStorage () {
    return JSON.parse(
      window.localStorage.getItem(this.configurationIdStorageKey) || '[]',
    )
  }

  /**
   * @description Stores configuration ids which have been added to cart.
   *
   * @param {GiftWithPurchaseConfiguration["id"][]} ids An array of gift with purchase configuration ids.
   * @returns {void}
   */
  set configurationIdStorage (ids) {
    window.localStorage.setItem(
      this.configurationIdStorageKey,
      JSON.stringify(ids),
    )
  }

  /**
   * @description Storage key used to store configuration ids which have been added to cart.
   *
   * @returns {string}
   */
  get configurationIdStorageKey () {
    return 'bao-gift-with-purchase_configuration-ids'
  }
}
window.BAO.GiftWithPurchase = new GiftWithPurchase()

/**
 * Reducer callback function type for creating a variant map.
 * @callback VariantMapReducer
 *
 * @param {Map<GiftVariant['id'], GiftVariant>} accumulator - The accumulating map of variants.
 * @param {GiftVariant} currentVariant - The current variant being processed.
 * @param {number} currentIndex - The index of the current variant in the array.
 * @param {GiftVariant[]} array - The original array of variants.
 * @returns {Map<GiftVariant['id'], GiftVariant>} The updated map.
 */

/**
 * @typedef {Object} GiftWithPurchaseProperties
 *
 * @property {boolean} _gift_with_purchase - Indicates if this is a gift with purchase item.
 * @property {string} _gift_with_purchase_id - The id of the gift with purchase configuration.
 * @property {number} _gift_with_purchase_discount_amount - The discount amount applied to the gift (as a decimal).
 */

/**
 * @typedef {Object} GiftVariant
 *
 * @property {number} id - The unique identifier for the variant.
 * @property {boolean} is_in_cart - Indicates if this variant is currently in the cart.
 * @property {string} line_item_key - The key of the line item.
 * @property {number} quantity - The quantity of this variant.
 * @property {GiftWithPurchaseProperties} properties - Special properties for gift with purchase items.
 */

/**
 * @typedef {Object} GiftItem
 *
 * @property {number} product_id - The unique identifier for the product.
 * @property {GiftVariant[]} variants - Array of available variants for this gift item.
 */

/**
 * @typedef {Object} GiftAction
 *
 * @property {GiftItem[]} items - Array of gift items available in this action.
 */

/**
 * @typedef {Object} GiftWithPurchaseConfiguration
 *
 * @property {string} id - The unique identifier for the gift with purchase configuration.
 * @property {string} type - CHOICE | AUTOMATIC - The type of configuration.
 * @property {boolean} is_eligible - Indicates if the customer is eligible for this gift with purchase.
 * @property {GiftAction[]} actions - Array of available gift actions.
 */

/**
 * @typedef {Object} CartItemKeyReference
 *
 * @property {string} line_item_key - The key of the line item.
 * @property {string} configuration_id - The unique identifier for the gift with purchase configuration.
 */

/**
 * @typedef {Object} GiftWithPurchaseData
 *
 * @property {boolean} is_cart_empty - Indicates whether the cart is currently empty or not.
 * @property {GiftWithPurchaseConfiguration[]} configurations - Array of configurations.
 */

/**
 * @typedef {Object} FetchResult
 *
 * @property {string} status - Indicates the status of the request.
 * @property {GiftWithPurchaseData} data - Object containing the required data for this module.
 */

/**
 * @typedef {Object} PotentialCartItem
 *
 * @property {number} id The variant's unique ID.
 * @property {number} quantity The quantity of items to be added to the cart.
 * @property {Object} [properties] Line item property key/values.
 */
